<template>
  <!-- A native button, not sp-button: `aria-controls` names the expansion cell's id in this shadow root, and an id
       reference does not resolve from the button sp-button renders in its own shadow root. -->
  <button
    type="button"
    part="toggle"
    class="sp-table-row-toggle"
    :class="classModifiers"
    :aria-expanded="expanded"
    :aria-controls="controlledId"
    :aria-label="label"
    @click="onClick"
  >
    <!-- The chevron glyph fills under half of its icon box, so the box overflows the button to draw a bigger chevron.
         The overflow leaves the row's height unchanged. -->
    <sp-icon class="sp-table-row-toggle__icon" name="chevron-down" size="medium"></sp-icon>
  </button>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  expanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Id of the cell the toggle opens. It is named in `aria-controls` only while the row is open, since the cell exists
   * only then.
   */
  controls: {
    type: String,
    required: true,
  },
  /**
   * Accessible name, which stays the same open or closed since `aria-expanded` carries the state.
   */
  label: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["toggle"]);

const classModifiers = computed(() => ({
  "--expanded": props.expanded,
}));

const controlledId = computed(() => (props.expanded ? props.controls : undefined));

function onClick() {
  emit("toggle");
}
</script>

<style lang="scss" scoped>
.sp-table-row-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--sp-ref-scale-24);
  block-size: var(--sp-ref-scale-24);
  padding: 0;
  border: 0;
  border-radius: var(--sp-ref-border-radius-xs);
  background: transparent;
  cursor: pointer;
  --sp-comp-icon-fill: var(--sp-sys-color-on-surface);

  &.--expanded &__icon {
    transform: rotate(180deg);
  }

  @media (prefers-reduced-motion: no-preference) {
    &__icon {
      transition: transform var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
    }
  }

  &:focus-visible {
    outline: var(--sp-comp-table-focus-outline-width, var(--sp-sys-state-focus-indicator-thickness)) solid
      var(--sp-comp-table-focus-outline-color, var(--sp-sys-state-focus-indicator-color));
  }
}
</style>
